import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { ProfilePage } from '@/pages/ProfilePage';
import { Loading, Button } from '@/components/common';
import { Language } from '@/types';
import { getTranslation, getStatusDisplay } from '@/utils';

const AppLayout: React.FC = () => {
  const [language, setLanguage] = useState<Language>('fr');
  const { user, userProfile, loading, signOut: handleSignOut } = useAuth();
  const t = getTranslation(language);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'fr' : 'en'));
  };

  if (loading) {
    return <Loading fullScreen text={t.loading} />;
  }

  if (!user || !userProfile) {
    return <LoginPage language={language} onLanguageToggle={toggleLanguage} />;
  }

  const statusDisplay = getStatusDisplay(userProfile.communityStatus, language);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header - Mobile-friendly with compact layout */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Logo and Title - Compact for mobile */}
            <a href="/" className="flex items-center space-x-2 no-underline hover:opacity-90 transition-opacity">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
                <span className="text-xl sm:text-2xl" role="img" aria-label="Helping hands">🤝</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-gray-900">
                  MutuAid
                </h1>
              </div>
            </a>

            {/* User Menu - Compact touch targets */}
            <div className="flex items-center gap-2">
              {/* Status Badge - Hidden on mobile */}
              <div className="hidden lg:flex items-center px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-sm font-medium text-blue-700">{statusDisplay}</span>
              </div>

              {/* Language Toggle - Compact */}
              <button
                onClick={toggleLanguage}
                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 min-h-[40px]"
                aria-label={language === 'en' ? 'Switch to French' : 'Switch to English'}
              >
                {language === 'en' ? 'FR' : 'EN'}
              </button>

              {/* Profile Button - Compact */}
              <a
                href="/profile"
                className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 no-underline transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 min-h-[40px]"
              >
                {userProfile.photoURL ? (
                  <img
                    src={userProfile.photoURL}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover border border-gray-200"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-sm text-blue-600 font-semibold">
                      {(userProfile.displayName || userProfile.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="hidden sm:inline">{t.myProfile}</span>
              </a>

              {/* Sign Out - Compact button */}
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                {t.signOut}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile-friendly padding */}
      <main id="main-content" className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <Routes>
          <Route path="/" element={<HomePage language={language} />} />
          <Route path="/profile" element={<ProfilePage language={language} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Footer - Compact */}
      <footer className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
          <div className="text-center text-sm text-gray-500">
            <p>
              © {new Date().getFullYear()} MutuAid. {t.rights}
            </p>
            <p className="mt-1 text-gray-400">{t.builtFor}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
